import { useDataEnvironment } from 'contexts/DataEnvironmentContext';
import { useQuery, useMutation } from 'react-query';
import {
  AssetWeightedPreviewItem,
  AssetWeightedPreviewRequest,
} from 'types/AssetWeighted';
import { Benchmark } from 'types/Benchmark';
import {
  CompositeWeightedPreview,
  CompositeWeightedRequestBody,
} from 'types/CompositeWeighted';
import { axiosClient } from './axiosClient';

const resource = '';

const benchmarks = {
  GetBenchmarks() {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['benchmarks', dataEnvironment?.id],
      () =>
        axiosClient.get<Benchmark[]>(
          `${resource}/${dataEnvironment?.id}/benchmarks`
        ),
      {
        refetchOnWindowFocus: false,
        select: ({ data }) =>
          data.sort((a, b) => {
            const nameA = a.name?.trim().toLocaleLowerCase() ?? '';
            const nameB = b.name?.trim().toLocaleLowerCase() ?? '';
            return nameA.localeCompare(nameB);
          }),
      }
    );
  },
  GetBenchmark<T>(id: string, options: any = {}) {
    const [dataEnvironment] = useDataEnvironment();
    return useQuery(
      ['benchmark', dataEnvironment?.id, id],
      () => {
        if (id) {
          return axiosClient.get<T>(
            `${resource}/${dataEnvironment?.id}/benchmarks/${id}?includeComponentNames=true`
          );
        } else return null;
      },
      { refetchOnWindowFocus: false, ...options }
    );
  },
  DeleteBenchmark() {
    const [dataEnvironment] = useDataEnvironment();
    return useMutation(
      ['benchmark-delete', dataEnvironment?.id],
      (id: string) =>
        axiosClient.delete<Benchmark>(
          `${resource}/${dataEnvironment?.id}/benchmarks/${id}`
        )
    );
  },
  SaveBenchmarks() {
    const [dataEnvironment] = useDataEnvironment();
    return useMutation(
      ['benchmarks-post', dataEnvironment?.id],
      ({ body }: any) => {
        if (body.hasOwnProperty('id') && body.id) {
          return axiosClient.put<Benchmark[]>(
            `${resource}/${dataEnvironment?.id}/benchmarks/${body.id}`,
            { ...body }
          );
        } else {
          return axiosClient.post<Benchmark[]>(
            `${resource}/${dataEnvironment?.id}/benchmarks`,
            { ...body }
          );
        }
      }
    );
  },
  PostBenchmarks() {
    const [dataEnvironment] = useDataEnvironment();
    return useMutation(
      ['benchmarks-post', dataEnvironment?.id],
      ({ body }: any) => {
        return axiosClient.post<Benchmark[]>(
          `${resource}/${dataEnvironment?.id}/benchmarks`,
          { ...body }
        );
      }
    );
  },
  GetCompositeWeightedTimeWindowData() {
    const [dataEnvironment] = useDataEnvironment();
    return useMutation(
      ['composite_weighted_time_window_preview', dataEnvironment?.id],
      (body: CompositeWeightedRequestBody) => {
        return axiosClient.post<CompositeWeightedPreview[]>(
          `${resource}/${dataEnvironment?.id}/composite_weighted_time_window_preview`,
          body
        );
      }
    );
  },
  GetAssetWeightedTimeWindowPreview() {
    const [dataEnvironment] = useDataEnvironment();
    return useMutation(
      ['get-asset-weighted-time-window-preview', dataEnvironment?.id],
      (body: AssetWeightedPreviewRequest) => {
        return axiosClient.post<AssetWeightedPreviewItem[]>(
          `${resource}/${dataEnvironment?.id}/asset_weighted_time_window_preview`,
          body
        );
      }
    );
  },
};

export default benchmarks;
